/**
 * Recent transactions widget for dashboard
 */

import { useMemo } from 'react';
import { ArrowDownCircle, ArrowUpCircle, ChevronRight, Plus, Receipt } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { useCashFlowEntries } from '../../../hooks/useCashFlowEntries';
import { formatCurrency } from '../../../lib/utils/format';
import { useCompanyCurrency } from '../../../hooks/useCompanyCurrency';
import { Badge } from '../../../components/ui/badge';

interface RecentTransactionsProps {
  companyIds: string[];
  userId: string;
  limit?: number;
  onNavigateToNewTransaction?: () => void;
  onNavigateToTransactions?: () => void;
}

export function RecentTransactions({ 
  companyIds, 
  userId,
  limit = 5,
  onNavigateToNewTransaction,
  onNavigateToTransactions
}: RecentTransactionsProps) {
  const companyCurrency = useCompanyCurrency();
  const { entries, loading } = useCashFlowEntries(companyIds, userId);

  const recentEntries = useMemo(() => {
    if (!entries.length) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return entries
      .filter((entry) => {
        // Use same date logic as display: payment_date > due_date > entry_date
        const relevantDate = new Date(entry.payment_date || entry.due_date || entry.entry_date || entry.created_at);
        return relevantDate >= today;
      })
      .sort((a, b) => {
        // Use same date logic as display: payment_date > due_date > entry_date
        const dateA = new Date(a.payment_date || a.due_date || a.entry_date || a.created_at);
        const dateB = new Date(b.payment_date || b.due_date || b.entry_date || b.created_at);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, limit);
  }, [entries, limit]);

  const todayTotal = useMemo(() => {
    const income = recentEntries
      .filter(e => e.entry_type === 'income')
      .reduce((sum, e) => sum + e.amount, 0);
    const expense = recentEntries
      .filter(e => e.entry_type === 'expense')
      .reduce((sum, e) => sum + e.amount, 0);
    return { income, expense, balance: income - expense };
  }, [recentEntries]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (recentEntries.length === 0) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle>Lançamentos de Hoje</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
            {/* Icon with gradient */}
            <div className="relative">
              <div className="absolute inset-0 bg-primary/10 blur-2xl rounded-full" />
              <div className="relative bg-primary/5 p-4 rounded-2xl border border-primary/20">
                <Receipt className="h-10 w-10 text-primary" />
              </div>
            </div>

            {/* Text */}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground max-w-[280px]">
                Nenhuma movimentação registrada ainda hoje. Adicione receitas ou despesas para acompanhar seu fluxo.
              </p>
            </div>

            {/* CTA */}
            <Button 
              size="default" 
              className="mt-2 gap-2" 
              onClick={onNavigateToNewTransaction}
            >
              <Plus className="h-4 w-4" />
              Novo Lançamento
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle>Lançamentos de Hoje</CardTitle>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {todayTotal.income > 0 && (
              <Badge variant="outline" className="border-success-500 text-success-700 dark:text-success-400 text-xs font-medium">
                +{formatCurrency(todayTotal.income, companyCurrency)}
              </Badge>
            )}
            {todayTotal.expense > 0 && (
              <Badge variant="outline" className="border-danger-500 text-danger-700 dark:text-danger-400 text-xs font-medium">
                -{formatCurrency(todayTotal.expense, companyCurrency)}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {recentEntries.map((entry) => {
          const isIncome = entry.entry_type === 'income';
          const Icon = isIncome ? ArrowUpCircle : ArrowDownCircle;

          return (
            <div
              key={entry.id}
              className="flex items-start gap-3 rounded-lg border p-3 transition-all hover:bg-muted/50 hover:shadow-sm hover:border-primary/20"
            >
              <div className={`p-1.5 rounded-lg shrink-0 ${
                isIncome 
                  ? 'bg-success-50 dark:bg-success-950' 
                  : 'bg-danger-50 dark:bg-danger-950'
              }`}>
                <Icon
                  className={`h-4 w-4 ${
                    isIncome
                      ? 'text-success-600 dark:text-success-400'
                      : 'text-danger-600 dark:text-danger-400'
                  }`}
                />
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <p className="text-sm font-medium line-clamp-2 leading-snug">{entry.description}</p>
                <p className="text-xs text-muted-foreground">
                  {entry.main_category}
                </p>
              </div>
              <div className="text-right shrink-0 space-y-1">
                <p
                  className={`text-sm font-semibold whitespace-nowrap ${
                    isIncome
                      ? 'text-success-600 dark:text-success-400'
                      : 'text-danger-600 dark:text-danger-400'
                  }`}
                >
                  {isIncome ? '+' : '-'}{formatCurrency(entry.amount, companyCurrency)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {entry.payment_status}
                </p>
              </div>
            </div>
          );
        })}

        <Button variant="ghost" size="sm" className="w-full gap-2 mt-4" onClick={onNavigateToTransactions}>
          Ver todos os lançamentos
          <ChevronRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
